<template>
  <div>
    <!--training progress start button-->
    <v-row
      v-if="
        !trainingRegistration.status ||
        trainingRegistration.status === 'REGISTERED'
      "
    >
      <v-col class="mb-4">
        <v-card flat class="pa-6 rounded-2">
          <v-row justify="center" align="center">
            <div class="text-start">
              <p class="textLight--text body-2 ma-0">
                {{ $t("course.interestedInThisCourse") }}
              </p>

              <p class="textLight--text text-h6 font-weight-bold ma-0">
                {{ $t("course.startLearning") }}
              </p>
            </div>

            <v-spacer />

            <v-btn
              depressed
              class="primary caption text-capitalize py-5 rounded-2"
              :loading="loading"
              @click="startTraining"
            >
              <v-icon size="14" class="me-1"> mdi-play-circle-outline </v-icon>

              {{ $t("course.start") }}
            </v-btn>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!--training progress resume button-->
    <v-row v-else-if="trainingRegistration.status === 'STARTED'">
      <v-col class="mb-8">
        <v-card flat class="pa-3 rounded-2">
          <v-row justify="center" align="end">
            <v-col class="text-start">
              <p class="textLight--text text-h6 ma-0 font-weight-medium">
                {{ $t("course.keepUpYourMomentum") }}
              </p>

              <div class="primary--text body-1 ma-0 font-weight-bold d-flex">
                <v-progress-linear
                  class="my-2 me-2"
                  :value="trainingProgressPercentage"
                  rounded
                  height="7"
                />
                {{ trainingProgressPercentage }}%
              </div>

              <p
                v-if="currentTrainingProgress"
                class="textLight--text body-2 ma-0"
                style="opacity: 0.5"
              >
                {{ $t("course.next") }}:

                <v-icon size="18">
                  {{
                    getTrainingModuleContentTypeIcon(
                      currentTrainingProgress.trainingModuleContent
                    )
                  }}
                </v-icon>

                {{
                  getTrainingModuleContentTitle(
                    currentTrainingProgress.trainingModuleContent
                  )
                }}
              </p>
            </v-col>

            <v-col
              v-if="!isResumed"
              cols="12"
              sm="4"
              md="12"
              lg="4"
              class="pt-0 pt-sm-3 ps-sm-0 pt-md-0 ps-md-3 text-end"
            >
              <v-btn
                depressed
                class="primary caption text-capitalize py-5 rounded-2"
                :loading="loading"
                @click="resumeTraining(currentTrainingProgress)"
              >
                <v-icon size="14" class="me-1">
                  mdi-play-circle-outline
                </v-icon>

                {{ $t("course.resume") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!--training progress modules-->
    <v-row
      v-for="(trainingModule, trainingModuleIndex) in trainingModules"
      :key="`courseProgress_trainingModule_${trainingModuleIndex}`"
    >
      <v-col class="mb-4">
        <!--training module title-->
        <v-row class="text-start">
          <v-col class="mb-3">
            <span class="textLight--text subtitle-1 font-weight-bold">
              {{ $t("course.module") }} {{ trainingModuleIndex + 1 }}:
              {{ getLocalisedValue(trainingModule.title) }}
            </span>
          </v-col>
        </v-row>

        <!--training module content icon, title, status-->
        <v-row no-gutters>
          <v-col
            v-for="(
              trainingModuleContent, trainingModuleContentIndex
            ) in trainingModule.trainingModuleContents"
            :key="`courseProgress_trainingModule_${trainingModuleIndex}_trainingModuleContent_${trainingModuleContentIndex}`"
            cols="12"
            class="text-start mb-3"
          >
            <v-card
              flat
              class="pa-2 rounded-2"
              :style="
                isTrainingModuleContentResumedAndOnGoing(
                  trainingModuleContent.trainingProgress
                ) ||
                isTrainingModuleContentSelected(
                  trainingModuleContent.trainingProgress
                )
                  ? 'border: solid 1px rgb(89 40 229);'
                  : ''
              "
              @click="
                isTrainingModuleContentCompleted(
                  trainingModuleContent.trainingProgress
                ) ||
                isTrainingModuleContentOnGoing(
                  trainingModuleContent.trainingProgress
                )
                  ? resumeTraining(trainingModuleContent.trainingProgress)
                  : null
              "
            >
              <v-row justify="center" align="center">
                <!--training module content icon & title-->
                <v-col>
                  <!--training module content icon-->
                  <v-icon
                    :color="
                      isTrainingModuleContentResumedAndOnGoing(
                        trainingModuleContent.trainingProgress
                      )
                        ? 'primary'
                        : 'textLight'
                    "
                    :style="
                      isTrainingModuleContentCompleted(
                        trainingModuleContent.trainingProgress
                      )
                        ? 'opacity: 0.3;'
                        : ''
                    "
                  >
                    {{
                      getTrainingModuleContentTypeIcon(trainingModuleContent)
                    }}
                  </v-icon>

                  <!--training module content title-->
                  <span
                    class="body-2"
                    :class="
                      isTrainingModuleContentResumedAndOnGoing(
                        trainingModuleContent.trainingProgress
                      )
                        ? 'primary--text'
                        : 'textLight--text'
                    "
                    :style="
                      isTrainingModuleContentCompleted(
                        trainingModuleContent.trainingProgress
                      )
                        ? 'opacity: 0.3; text-decoration: line-through;'
                        : ''
                    "
                  >
                    {{ getTrainingModuleContentTitle(trainingModuleContent) }}
                  </span>
                </v-col>

                <!--training module content duration & status-->
                <v-col
                  cols="12"
                  sm="auto"
                  class="text-end pt-0 pt-sm-3 ps-sm-0"
                >
                  <!--training module content duration-->
                  <span
                    v-if="!trainingModuleContent.trainingProgress"
                    class="textLight--text body-2 justify-center align-center"
                    style="opacity: 0.2"
                  >
                    {{
                      `${trainingModuleContent.duration} ${$t(
                        "course.minutes"
                      )}`
                    }}
                  </span>

                  <!--training module content lock icon-->
                  <v-icon
                    v-if="!trainingModuleContent.trainingProgress"
                    size="18"
                    color="textLight"
                    style="opacity: 0.5"
                  >
                    mdi-lock-outline
                  </v-icon>

                  <!--training module content playing icon-->
                  <v-icon
                    v-else-if="
                      isTrainingModuleContentResumedAndOnGoing(
                        trainingModuleContent.trainingProgress
                      )
                    "
                    color="primary"
                  >
                    mdi-play-outline
                  </v-icon>

                  <!--training module content resume button-->
                  <v-btn
                    v-else-if="
                      isTrainingModuleContentOnGoing(
                        trainingModuleContent.trainingProgress
                      )
                    "
                    depressed
                    class="primary caption text-capitalize py-5 rounded-2"
                    :loading="loading"
                  >
                    <v-icon size="14" class="me-1">
                      mdi-play-circle-outline
                    </v-icon>

                    {{ $t("course.resume") }}
                  </v-btn>

                  <!--training module content completed icon-->
                  <v-icon
                    v-else-if="
                      isTrainingModuleContentCompleted(
                        trainingModuleContent.trainingProgress
                      )
                    "
                    size="18"
                    color="success"
                  >
                    mdi-check-circle-outline
                  </v-icon>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { updateTrainingProgress } from "../methods/updateTrainingProgress";
import { createOrUpdateTrainingAttendance } from "../methods/createOrUpdateTrainingAttendance";

export default {
  name: "CourseProgress",
  props: {
    trainingRegistration: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      isResumed: false,
      selectedTrainingProgress: null,
    };
  },
  computed: {
    trainingModules() {
      return this.trainingRegistration.training?.trainingModules ?? [];
    },
    trainingProgressPercentage() {
      return this.getTrainingProgressPercentage(this.trainingRegistration);
    },
    currentTrainingProgress() {
      return this.trainingRegistration.trainingProgresses.find(
        x => x.status === "ON_GOING"
      );
    },
  },
  methods: {
    isTrainingModuleContentResumedAndOnGoing(trainingProgress) {
      return (
        this.isResumed && this.isTrainingModuleContentOnGoing(trainingProgress)
      );
    },
    isTrainingModuleContentOnGoing(trainingProgress) {
      return trainingProgress && trainingProgress.status === "ON_GOING";
    },
    isTrainingModuleContentCompleted(trainingProgress) {
      return trainingProgress && trainingProgress.status === "COMPLETED";
    },
    isTrainingModuleContentSelected(trainingProgress) {
      return (
        this.selectedTrainingProgress &&
        trainingProgress &&
        this.selectedTrainingProgress._id === trainingProgress._id
      );
    },
    startTraining() {
      if (!this.loading) {
        this.loading = true;

        const input = {
          trainingRegistrationId: this.trainingRegistration._id,
        };

        if (this.trainingRegistration.status === "REGISTERED") {
          input.trainingRegistrationStatus = "STARTED";
        }

        if (
          !this.trainingRegistration?.trainingProgresses?.length &&
          this.trainingRegistration?.training?.trainingModules?.length
        ) {
          const firstTrainingModule =
            this.trainingRegistration.training.trainingModules[0];

          if (firstTrainingModule.trainingModuleContents?.length) {
            const firstTrainingModuleContent =
              firstTrainingModule.trainingModuleContents[0];

            input.nextTrainingModuleId = firstTrainingModule._id;
            input.nextTrainingModuleContentId = firstTrainingModuleContent._id;
          }

          if (!this.trainingRegistration.sortedTrainingModules?.length) {
            input.trainingRegistrationSortedTrainingModules =
              this.trainingRegistration.training.trainingModules.map(tm => {
                return {
                  trainingModule: tm._id,
                  title: tm.title,
                  description: tm.description,
                  trainingModuleContents: tm.trainingModuleContents?.map(
                    tmc => tmc._id
                  ),
                };
              });
          }

          input.trainingRegistrationProgressPercentage = 0;
        }

        updateTrainingProgress(input).then(response => {
          if (response.data) {
            this.resumeTraining(
              response.data.trainingProgresses.find(
                x => x.status === "ON_GOING"
              ),
              false
            );
          } else if (response.error) {
            this.$emit("onError");
          }

          this.loading = false;
        });
      }
    },
    resumeTraining(trainingProgress, updateAttendance = true) {
      if (
        this.trainingRegistration?.status === "REGISTERED" ||
        !this.trainingRegistration?.trainingProgresses?.length
      ) {
        return this.startTraining();
      }

      const doResume = () => {
        this.isResumed = true;
        this.selectedTrainingProgress =
          trainingProgress || this.currentTrainingProgress;
        this.$emit("onSelected", this.selectedTrainingProgress);
      };

      const shouldUpdateAttendance =
        updateAttendance &&
        (!this.trainingRegistration.trainingAttendance ||
          this.trainingRegistration.trainingAttendance.attendances.findIndex(
            x =>
              this.$moment.utc(parseInt(x.date)).format("yyyy-MM-DD") ===
              this.$moment.utc().format("yyyy-MM-DD")
          ) < 0);

      if (!this.loading && shouldUpdateAttendance) {
        this.loading = true;

        const input = {
          trainingRegistrationId: this.trainingRegistration._id,
          attendances: [
            {
              date: this.$moment.utc().format("yyyy-MM-DD"),
              status: "PRESENT",
            },
          ],
        };

        createOrUpdateTrainingAttendance(input).then(response => {
          if (response.data) {
            doResume();
          } else if (response.error) {
            this.$emit("onError");
          }

          this.loading = false;
        });
      } else {
        doResume();
      }
    },
  },
};
</script>

<style lang="scss" scoped />
